import React, { useContext, useEffect, useState } from "react";
import { toast } from "../../utils/toast";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import TableScrollShell from "../common/TableScrollShell";
import { getThemedTableShell } from "../../utils/tableStyles";

const PaymentTable = ({ pevData, setIsEdit, isEdit, setNewData, newData = [], setIsSaveChanges, isSaveChanges }) => {  // Ensure newData has a default value

    const [instalments, setInstalments] = useState(pevData.instalments || []);
    const { pdfData, setPdfData, langMode, theme } = useContext(AppContext);
    const tableStyles = getThemedTableShell(theme);
    const isRtl = langMode === "ar" || langMode === "ur";
    const headerBgClass =
        theme === "dark"
            ? "bg-gray-800"
            : theme === "high-contrast"
                ? "bg-gray-100"
                : "bg-gradient-to-r from-gray-50 to-gray-100";
    const cardClass =
        theme === "dark"
            ? "rounded-2xl border border-gray-700 bg-gray-800/50"
            : theme === "high-contrast"
                ? "rounded-2xl border-2 border-black bg-white"
                : "rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05),0_1px_3px_rgba(16,24,40,0.05)]";
    const tableCardClass =
        theme === "dark"
            ? " border border-gray-700 bg-gray-800/40"
            : theme === "high-contrast"
                ? " border-2 border-black bg-white"
                : " border border-slate-200 bg-white shadow-sm";
    const totalPaymentAmount = instalments.reduce((sum, inst) => sum + (parseFloat(inst.paymentAmount) || 0), 0);
    const amountToAdjust = parseFloat(pevData.outstandingAfterAdvance) - totalPaymentAmount;

    useEffect(() => {
        if (!isEdit && instalments) {
            setPdfData(prev => ({
                ...prev,
                instalments: instalments,
                currentOutstanding: pevData.currentOutstanding,
                outstandingAfterAdvance: pevData.outstandingAfterAdvance
            }));
            const formattedData = instalments.map(inst => ({
                paymentDate: new Date(inst.paymentDate).toISOString(),
                paymentAmount: parseFloat(inst.paymentAmount) || 0
            }));
            setNewData(formattedData)
        }
    }, [instalments, isEdit]);

    const handleAmountChange = (index, value) => {
        let newAmount = parseFloat(value) || 0;
        let newInstalments = [...instalments];

        const totalOutstanding = parseFloat(pevData.outstandingAfterAdvance);

        let newTotalPaymentAmount = instalments.reduce((sum, inst, i) =>
            sum + (i === index ? newAmount : parseFloat(inst.paymentAmount) || 0), 0
        );

        if (newAmount >= totalOutstanding) {
            toast.error(translations[langMode].youcannotpaythefullamount);
            return;
        }

        let remainingInstallments = instalments.length - 1;
        let remainingBalance = totalOutstanding - newAmount;

        if (remainingBalance / remainingInstallments < 1) {
            toast.error(translations[langMode].eachinstallmentmusthave);
            return;
        }

        newInstalments[index].paymentAmount = newAmount;
        const remainingIndexes = newInstalments
            .map((_, i) => i)
            .filter(i => i !== index);

        if (remainingIndexes.length > 0) {
            const lastIndex = remainingIndexes[remainingIndexes.length - 1];
            const totalOfOthers = remainingIndexes
                .filter(i => i !== lastIndex)
                .reduce((sum, i) => sum + (parseFloat(newInstalments[i].paymentAmount) || 0), 0);
            const finalAmount = parseFloat((remainingBalance - totalOfOthers));
            newInstalments[lastIndex].paymentAmount = finalAmount;
        }

        setInstalments(newInstalments);

        newTotalPaymentAmount = newInstalments.reduce((sum, inst) => sum + (parseFloat(inst.paymentAmount) || 0), 0);
        const updatedAmountToAdjust = totalOutstanding - newTotalPaymentAmount;

        setIsEdit(true);

        setPdfData(prev => ({
            ...prev,
            instalments: newInstalments,
            isEMIMismatch: Math.abs(updatedAmountToAdjust) > 0.01,
        }));
    };

    const handleEditClick = () => {
        setIsSaveChanges(true);
        setIsEdit(true);
    };

    const handleSaveClick = () => {
        console.log("handleSaveClick function triggered!");
        const formattedData = instalments.map(inst => ({
            paymentDate: new Date(inst.paymentDate).toISOString(),
            paymentAmount: parseFloat(inst.paymentAmount) || 0
        }));
        console.log("Saved Data:", formattedData);
        setPdfData(prev => ({
            ...prev,
            instalments: instalments,
            currentOutstanding: pevData.currentOutstanding,
            outstandingAfterAdvance: pevData.outstandingAfterAdvance,
            isEMIMismatch: false, // Ensure button is enabled after saving
        }));
        setNewData(formattedData);
        setIsSaveChanges(false); // Enable the button after saving
        toast.success(translations[langMode].changesavedsuc);
    };

    return (
        <div>
            {totalPaymentAmount < 0 && (
                <div className={`font-semibold mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600`}>
                    {
                        `You have entered more than the total outstanding balance by ${Math.abs(totalPaymentAmount).toFixed(2)}. Please reduce the EMI amount.`
                    }
                </div>
            )}
            {Math.abs(amountToAdjust) > 0.01 && (
                <div className={`font-semibold mb-3 rounded-lg border px-3 py-2 text-sm ${amountToAdjust > 0 ? "border-red-200 bg-red-50 text-red-600" : "border-blue-200 bg-blue-50 text-blue-600"}`}>
                    {amountToAdjust > 0 ?
                        `You have entered less EMI amount. Please adjust ${amountToAdjust.toFixed(2)} more to match the outstanding balance.`
                        :
                        `You have entered more than the total outstanding balance by ${Math.abs(amountToAdjust).toFixed(2)}. Please reduce the EMI amount.`}
                </div>
            )}

            <div className={`flex flex-wrap items-center justify-between gap-3 ${isRtl ? "flex-row-reverse text-right" : ""}`}>
                <div>
                    <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                        {translations[langMode].payDetail}
                    </h2>
                    <p className="text-xs text-gray-500">
                        {translations[langMode]?.instalDetail ?? "Installment Details"}
                    </p>
                </div>
                {!isSaveChanges ? (
                    <CommonButton colorIntent="primary"
                        onClick={handleEditClick}
                        variant='primary'
                        tooltipText={translations[langMode].editthepaymentamount}
                        tooltipPosition="top"
                    >
                        {translations[langMode].edit}
                    </CommonButton>
                ) : (
                    <CommonButton colorIntent="success"
                        onClick={handleSaveClick}
                        variant='success'
                        tooltipText={translations[langMode].savethechagesmadeto}
                        tooltipPosition="top"
                    >
                        {translations[langMode].save}
                    </CommonButton>
                )}
            </div>

            <div className={`mt-4 `}>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                        {translations[langMode].instalDetail}
                    </h3>
                </div>
                <TableScrollShell
                    theme={theme}
                    className="relative z-0"
                    innerClassName="!max-h-none !border-0 !shadow-none !rounded-none"
                >
                    <table className={`${tableStyles.table} border-collapse`}>
                        <thead className={tableStyles.thead}>
                            <tr>
                                <th className={`${tableStyles.th} border border-gray-200 text-center`}>{translations[langMode].AfterAdv}</th>
                                <th className={`${tableStyles.th} border border-gray-200 text-center`}>{translations[langMode].currentOut}</th>
                            </tr>
                        </thead>
                        <tbody className={tableStyles.tbody}>
                            <tr className={`${tableStyles.tr} text-center`}>
                                <td className={`${tableStyles.td} border border-gray-200`}>{totalPaymentAmount < 0 ? 0 : pevData.outstandingAfterAdvance}</td>
                                <td className={`${tableStyles.td} border border-gray-200`}>{pevData.currentOutstanding}</td>
                            </tr>
                        </tbody>
                    </table>
                </TableScrollShell>
            </div>

            <div className={`mt-4 `}>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                        {translations[langMode]?.payDetail ?? "Payment Details"}
                    </h3>
                </div>
                <TableScrollShell
                    theme={theme}
                    className="relative z-10"
                    innerClassName="!max-h-none !border-0 !shadow-none !rounded-none"
                >
                    <table className={`${tableStyles.table} isolate w-full min-w-full table-fixed border-separate border-spacing-0`}>
                        <thead className={`${tableStyles.thead} sticky top-0 z-30 ${headerBgClass}`}>
                            <tr className={headerBgClass}>
                        <th className={`${tableStyles.th} w-[30%] sm:w-[28%] sticky top-0 z-40 border border-gray-200 text-center ${headerBgClass}`}>{translations[langMode].payDate}</th>
                        <th className={`${tableStyles.th} w-[22%] sm:w-[16%] sticky top-0 z-40 border border-gray-200 text-center ${headerBgClass}`}>{translations[langMode].Amount}</th>
                        <th className={`${tableStyles.th} w-[48%] sm:w-[56%] sticky top-0 z-40 border border-gray-200 text-center ${headerBgClass}`}>{translations[langMode].Notes}</th>
                            </tr>
                        </thead>
                        <tbody className={tableStyles.tbody}>
                            {instalments.map((inst, index) => (
                                <tr key={index} className="text-center">
                                    <td className="border border-gray-300 px-3 py-2.5 text-sm break-words sm:px-4">
                                        {new Date(inst.paymentDate).toLocaleDateString()}
                                    </td>
                                    <td className="border border-gray-300 px-3 py-2.5 text-sm break-words sm:px-4">
                                        {!isSaveChanges ? (
                                            <span>{totalPaymentAmount < 0 ? 0 : inst.paymentAmount}</span>
                                        ) : (
                                            <input
                                                type="number"
                                                value={inst.paymentAmount}
                                                step="0.01"
                                                className="h-[42px] w-full rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
                                                onChange={(e) => handleAmountChange(index, e.target.value)}
                                            />
                                        )}
                                    </td>
                                    <td className="border border-gray-300 px-3 py-2.5 text-sm whitespace-normal break-words sm:px-4">{inst.notes || "N/A"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </TableScrollShell>
            </div>
        </div>
    );
};

export default PaymentTable;
